import { useMemo, type ReactNode } from 'react';
import { useHydrated } from 'remix-utils';
import {
    MagicIsland,
    MagicIslandProvider,
    useIslandFallback,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { ArticleReadAloudBar } from './ArticleReadAloudBar';
import { useScrolledPastFirstScreen } from '../hook/use-scrolled-past-first-screen';

interface ArticleMagicIslandProps {
    children: ReactNode;
    shareLink: string;
    articleID: number;
    articleTitle: string;
    thumbnailUrl?: string;
    /** The article's opening paragraph, read after the headline. */
    intro?: string;
}

/**
 * Holds the read-aloud bar in the island for the whole article.
 *
 * Registered as the island's fallback rather than against a section: the offer
 * to listen stands wherever the reader is on the page, which is the one thing
 * that does not change as they scroll. A section can still claim the band over
 * it if one ever needs to.
 */
const ReadAloudFallback = ({
    shareLink,
    articleID,
    articleTitle,
    thumbnailUrl,
    intro,
}: Omit<ArticleMagicIslandProps, 'children'>) => {
    const isVisible = useScrolledPastFirstScreen();

    // Null until the reader is past the first screen: the controller treats no
    // fallback as nothing to show, so the island stays hidden and then plays
    // its own entrance when the mode arrives.
    const mode = useMemo<IslandMode | null>(
        () =>
            isVisible
                ? {
                      kind: 'custom',
                      surface: 'article-read-aloud',
                      content: (
                          <ArticleReadAloudBar
                              shareLink={shareLink}
                              articleID={articleID}
                              articleTitle={articleTitle}
                              thumbnailUrl={thumbnailUrl}
                              intro={intro}
                          />
                      ),
                  }
                : null,
        [isVisible, shareLink, articleID, articleTitle, thumbnailUrl, intro],
    );

    useIslandFallback(mode);
    return null;
};

/**
 * Article-detail wrapper for the Magic Island, the counterpart to the product
 * and project pages' own. Client-only (`useHydrated`), so the server renders
 * nothing and there is no hydration shift.
 */
export const ArticleMagicIsland = ({
    children,
    shareLink,
    articleID,
    articleTitle,
    thumbnailUrl,
    intro,
}: ArticleMagicIslandProps) => {
    const hydrated = useHydrated();

    return (
        <MagicIslandProvider>
            {children}
            {hydrated ? (
                <>
                    <ReadAloudFallback
                        shareLink={shareLink}
                        articleID={articleID}
                        articleTitle={articleTitle}
                        thumbnailUrl={thumbnailUrl}
                        intro={intro}
                    />
                    <MagicIsland />
                </>
            ) : null}
        </MagicIslandProvider>
    );
};
